"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.apply = apply;
exports.compose = compose;
exports.composeCssTransform = composeCssTransform;
exports.create = create;
exports.determinant = determinant;
exports.invert = invert;
exports.makeInverse = makeInverse;
exports.makeScale = makeScale;
exports.multiply = multiply;
exports.reset = reset;
exports.rotate = rotate;
exports.scale = scale;
exports.set = set;
exports.setFromArray = setFromArray;
exports.toString = toString;
exports.translate = translate;

var _has = require("./has.js");

var _asserts = require("./asserts.js");

/**
 * @module ol/transform
 */

/**
 * An array representing an affine 2d transformation for use with
 * {@link module:ol/transform} functions. The array has 6 elements.
 * @typedef {!Array<number>} Transform
 * @api
 */

/**
 * Collection of affine 2d transformation functions. The functions work on an
 * array of 6 elements. The element order is compatible with the [SVGMatrix
 * interface](https://developer.mozilla.org/en-US/docs/Web/API/SVGMatrix) and is
 * a subset (elements a to f) of a 3×3 matrix:
 * ```
 * [ a c e ]
 * [ b d f ]
 * [ 0 0 1 ]
 * ```
 */

/**
 * @private
 * @type {Transform}
 */
var tmp_ = new Array(6);
/**
 * Create an identity transform.
 * @return {!Transform} Identity transform.
 */

function create() {
  return [1, 0, 0, 1, 0, 0];
}
/**
 * Resets the given transform to an identity transform.
 * @param {!Transform} transform Transform.
 * @return {!Transform} Transform.
 */


function reset(transform) {
  return set(transform, 1, 0, 0, 1, 0, 0);
}
/**
 * Multiply the underlying matrices of two transforms and return the result in
 * the first transform.
 * @param {!Transform} transform1 Transform parameters of matrix 1.
 * @param {!Transform} transform2 Transform parameters of matrix 2.
 * @return {!Transform} transform1 multiplied with transform2.
 */


function multiply(transform1, transform2) {
  var a1 = transform1[0];
  var b1 = transform1[1];
  var c1 = transform1[2];
  var d1 = transform1[3];
  var e1 = transform1[4];
  var f1 = transform1[5];
  var a2 = transform2[0];
  var b2 = transform2[1];
  var c2 = transform2[2];
  var d2 = transform2[3];
  var e2 = transform2[4];
  var f2 = transform2[5];
  transform1[0] = a1 * a2 + c1 * b2;
  transform1[1] = b1 * a2 + d1 * b2;
  transform1[2] = a1 * c2 + c1 * d2;
  transform1[3] = b1 * c2 + d1 * d2;
  transform1[4] = a1 * e2 + c1 * f2 + e1;
  transform1[5] = b1 * e2 + d1 * f2 + f1;
  return transform1;
}
/**
 * Set the transform components a-f on a given transform.
 * @param {!Transform} transform Transform.
 * @param {number} a The a component of the transform.
 * @param {number} b The b component of the transform.
 * @param {number} c The c component of the transform.
 * @param {number} d The d component of the transform.
 * @param {number} e The e component of the transform.
 * @param {number} f The f component of the transform.
 * @return {!Transform} Matrix with transform applied.
 */


function set(transform, a, b, c, d, e, f) {
  transform[0] = a;
  transform[1] = b;
  transform[2] = c;
  transform[3] = d;
  transform[4] = e;
  transform[5] = f;
  return transform;
}
/**
 * Set transform on one matrix from another matrix.
 * @param {!Transform} transform1 Matrix to set transform to.
 * @param {!Transform} transform2 Matrix to set transform from.
 * @return {!Transform} transform1 with transform from transform2 applied.
 */


function setFromArray(transform1, transform2) {
  transform1[0] = transform2[0];
  transform1[1] = transform2[1];
  transform1[2] = transform2[2];
  transform1[3] = transform2[3];
  transform1[4] = transform2[4];
  transform1[5] = transform2[5];
  return transform1;
}
/**
 * Transforms the given coordinate with the given transform returning the
 * resulting, transformed coordinate. The coordinate will be modified in-place.
 *
 * @param {Transform} transform The transformation.
 * @param {import("./coordinate.js").Coordinate|import("./pixel.js").Pixel} coordinate The coordinate to transform.
 * @return {import("./coordinate.js").Coordinate|import("./pixel.js").Pixel} return coordinate so that operations can be
 *     chained together.
 */


function apply(transform, coordinate) {
  var x = coordinate[0];
  var y = coordinate[1];
  coordinate[0] = transform[0] * x + transform[2] * y + transform[4];
  coordinate[1] = transform[1] * x + transform[3] * y + transform[5];
  return coordinate;
}
/**
 * Applies rotation to the given transform.
 * @param {!Transform} transform Transform.
 * @param {number} angle Angle in radians.
 * @return {!Transform} The rotated transform.
 */


function rotate(transform, angle) {
  var cos = Math.cos(angle);
  var sin = Math.sin(angle);
  return multiply(transform, set(tmp_, cos, sin, -sin, cos, 0, 0));
}
/**
 * Applies scale to a given transform.
 * @param {!Transform} transform Transform.
 * @param {number} x Scale factor x.
 * @param {number} y Scale factor y.
 * @return {!Transform} The scaled transform.
 */


function scale(transform, x, y) {
  return multiply(transform, set(tmp_, x, 0, 0, y, 0, 0));
}
/**
 * Creates a scale transform.
 * @param {!Transform} target Transform to overwrite.
 * @param {number} x Scale factor x.
 * @param {number} y Scale factor y.
 * @return {!Transform} The scale transform.
 */


function makeScale(target, x, y) {
  return set(target, x, 0, 0, y, 0, 0);
}
/**
 * Applies translation to the given transform.
 * @param {!Transform} transform Transform.
 * @param {number} dx Translation x.
 * @param {number} dy Translation y.
 * @return {!Transform} The translated transform.
 */


function translate(transform, dx, dy) {
  return multiply(transform, set(tmp_, 1, 0, 0, 1, dx, dy));
}
/**
 * Creates a composite transform given an initial translation, scale, rotation, and
 * final translation (in that order only, not commutative).
 * @param {!Transform} transform The transform (will be modified in place).
 * @param {number} dx1 Initial translation x.
 * @param {number} dy1 Initial translation y.
 * @param {number} sx Scale factor x.
 * @param {number} sy Scale factor y.
 * @param {number} angle Rotation (in counter-clockwise radians).
 * @param {number} dx2 Final translation x.
 * @param {number} dy2 Final translation y.
 * @return {!Transform} The composite transform.
 */


function compose(transform, dx1, dy1, sx, sy, angle, dx2, dy2) {
  var sin = Math.sin(angle);
  var cos = Math.cos(angle);
  transform[0] = sx * cos;
  transform[1] = sy * sin;
  transform[2] = -sx * sin;
  transform[3] = sy * cos;
  transform[4] = dx2 * sx * cos - dy2 * sx * sin + dx1;
  transform[5] = dx2 * sy * sin + dy2 * sy * cos + dy1;
  return transform;
}
/**
 * Creates a composite transform given an initial translation, scale, rotation, and
 * final translation (in that order only, not commutative). The resulting transform
 * string can be applied as `transform` property of an HTMLElement's style.
 * @param {number} dx1 Initial translation x.
 * @param {number} dy1 Initial translation y.
 * @param {number} sx Scale factor x.
 * @param {number} sy Scale factor y.
 * @param {number} angle Rotation (in counter-clockwise radians).
 * @param {number} dx2 Final translation x.
 * @param {number} dy2 Final translation y.
 * @return {string} The composite css transform.
 * @api
 */


function composeCssTransform(dx1, dy1, sx, sy, angle, dx2, dy2) {
  return toString(compose(create(), dx1, dy1, sx, sy, angle, dx2, dy2));
}
/**
 * Invert the given transform.
 * @param {!Transform} source The source transform to invert.
 * @return {!Transform} The inverted (source) transform.
 */


function invert(source) {
  return makeInverse(source, source);
}
/**
 * Invert the given transform.
 * @param {!Transform} target Transform to be set as the inverse of
 *     the source transform.
 * @param {!Transform} source The source transform to invert.
 * @return {!Transform} The inverted (target) transform.
 */


function makeInverse(target, source) {
  var det = determinant(source);
  (0, _asserts.assert)(det !== 0, 32); // Transformation matrix cannot be inverted

  var a = source[0];
  var b = source[1];
  var c = source[2];
  var d = source[3];
  var e = source[4];
  var f = source[5];
  target[0] = d / det;
  target[1] = -b / det;
  target[2] = -c / det;
  target[3] = a / det;
  target[4] = (c * f - d * e) / det;
  target[5] = -(a * f - b * e) / det;
  return target;
}
/**
 * Returns the determinant of the given matrix.
 * @param {!Transform} mat Matrix.
 * @return {number} Determinant.
 */


function determinant(mat) {
  return mat[0] * mat[3] - mat[1] * mat[2];
}
/**
 * @type {HTMLElement}
 * @private
 */


var transformStringDiv;
/**
 * A rounded string version of the transform.  This can be used
 * for CSS transforms.
 * @param {!Transform} mat Matrix.
 * @return {string} The transform as a string.
 */

function toString(mat) {
  var transformString = 'matrix(' + mat.join(', ') + ')';

  if (_has.WORKER_OFFSCREEN_CANVAS) {
    return transformString;
  }

  var node = transformStringDiv || (transformStringDiv = document.createElement('div'));
  node.style.transform = transformString;
  return node.style.transform;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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